// src/pages/Login.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import API from '../api';
import appConfig from '../config/appConfig';
import { useBranding } from '../context/BrandingContext';
import BrandingLogo from '../components/BrandingLogo';
import { AUTH_NOTICE_KEY } from '../utils/sessionManager';
import {
  setAuth,
  isAuthenticated,
  shouldPromptRole,
  getActiveRole,
  getRoles,
  setActiveRole,
  targetPathForRole,
  syncRolesFromServer,
} from '../utils/auth';
import { resetSetupCache } from '../components/RequireAuth';
import { useTranslation } from 'react-i18next';

const Login = () => {
  const { t } = useTranslation();
  const { appName, ready, hydrated } = useBranding();
  const showName = appName || (ready ? appConfig.appName : null);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [errorKey, setErrorKey] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const notice = sessionStorage.getItem(AUTH_NOTICE_KEY);
    if (notice) {
      const legacy = {
        'Your session has expired. Please log in again.': 'auth.sessionExpired',
        'Your session is no longer valid. Please log in again.': 'auth.sessionInvalid',
        'Your session expired due to inactivity. Please log in again.': 'auth.sessionInactive',
      };
      const key = notice.startsWith('auth.') ? notice : legacy[notice];
      if (key) setErrorKey(key);
      else setError(notice);
      sessionStorage.removeItem(AUTH_NOTICE_KEY);
    }
  }, []);

  const displayError = errorKey ? t(errorKey) : error;

  // If already logged in, route away appropriately
  useEffect(() => {
    if (!isAuthenticated()) return;

    if (shouldPromptRole()) {
      navigate('/select-role', { replace: true });
      return;
    }
    const active = getActiveRole();
    if (active) {
      const from = location.state?.from?.pathname;
      navigate(from || targetPathForRole(active), { replace: true });
    } else {
      const roles = getRoles();
      if (roles.length === 1) {
        setActiveRole(roles[0]);
        navigate(targetPathForRole(roles[0]), { replace: true });
      }
    }
  }, [navigate, location]);

  const handleChange = (e) =>
    setCredentials({ ...credentials, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorKey('');
    try {
      const res = await API.post('/auth/login', credentials);
      const token = res?.data?.token || res?.data?.accessToken || '';
      if (!token) throw new Error('Invalid login response');

      // Fresh login — drop stale role/setup state from a previous session or DB reset
      resetSetupCache();
      localStorage.removeItem('activeRole');

      setAuth(token);

      // Fetch authoritative roles list and cache it
      await syncRolesFromServer();

      // Decide navigation
      if (shouldPromptRole()) {
        navigate('/select-role', { replace: true });
        return;
      }

      const active = getActiveRole();
      const from = location.state?.from?.pathname;

      if (active) {
        navigate(from || targetPathForRole(active), { replace: true });
      } else {
        const roles = getRoles();
        if (roles.length === 1) {
          setActiveRole(roles[0]);
          navigate(targetPathForRole(roles[0]), { replace: true });
        } else {
          navigate('/select-role', { replace: true });
        }
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        setError(t('auth.tooManyAttempts'));
      } else if (err?.response?.status === 403) {
        setError(t('auth.accountDisabled', { defaultValue: t('auth.invalid') }));
      } else {
        setError(t('auth.invalid'));
      }
    }
  };

  return (
    <div className="w-full flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-accent-50 px-4 py-10">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-lg mb-4 p-2">
            <BrandingLogo
              className="w-full h-full object-contain rounded-xl"
              boxClassName="w-full h-full rounded-xl"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('auth.welcome')}</h1>
          {showName ? (
            <p className="text-gray-600">{t('auth.signInTo', { name: showName })}</p>
          ) : (
            <div className="mx-auto h-5 w-48 rounded bg-gray-200 animate-pulse" aria-hidden="true" />
          )}
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          {displayError && (
            <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
              <svg className="w-5 h-5 text-danger-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-danger-800">{displayError}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.username')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  id="username"
                  type="text"
                  name="username"
                  value={credentials.username}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder={t('auth.enterUsername')}
                  autoFocus
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  className="input pl-10 pr-10"
                  placeholder={t('auth.enterPassword')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full btn-primary py-3 text-base font-semibold shadow-md hover:shadow-lg"
            >
              {t('auth.login')}
            </button>

            {/* Forgot Password Link */}
            <div className="text-center pt-2">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline"
              >
                {t('auth.forgot')}
              </Link>
            </div>
          </form>
          {/* Privacy footer */}
          <p className="mt-6 text-center text-xs text-gray-400">
            {t('auth.agreePrefix')}{' '}
            <a href="/privacy-policy" className="underline hover:text-gray-600">{t('auth.privacyPolicy')}</a>
            {' '}{t('auth.agreeAnd')}{' '}
            <a href="/terms" className="underline hover:text-gray-600">{t('auth.terms')}</a>.
          </p>
        </div>

      </div>
    </div>
  );
};

export default Login;
